import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray, UntypedFormControl,
  FormControl
} from '@angular/forms';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { ACCOUNT_ROLES, ACCOUNT_TYPES, VALIDATORS } from 'app/constants';
import { Account, Branch, Organization } from 'graphql/models/generated';
import { CurrentAccountRoles } from 'models/account.model';
import { BaseComponent } from 'shared/base/base.component';
import { PermissionsService } from 'shared/services/permissions.service';
import { SettingsService } from 'shared/services/settings.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-branch-invite-member',
  templateUrl: './invite-member.component.html',
  styleUrls: ['./invite-member.component.scss']
})
export class BranchInviteMemberComponent extends BaseComponent implements OnInit {
  form!: UntypedFormGroup;

  MAX_AMOUNT_INVITEES = 10;

  canSubmit = false;

  branchId?: string;

  dropdownData = {
    branches: {},
    roles: ACCOUNT_ROLES,
    accountTypes: ACCOUNT_TYPES,
    organizations: {}
  };

  isDealer!: boolean;

  branches!: Branch[];

  isSystemAdmin = false;
  organizations: Organization[] = [];

  accountManufacturer?: string; // PRUEM / GARANT / etc

  get invitees(): UntypedFormArray {
    return this.form.controls.invitees as UntypedFormArray;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private settingsService: SettingsService,
    private storeService: StoreService,
    private permissions: PermissionsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.setFormReady(false);

    combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.getAccount(),
      this.storeService.getCurrentBranch(),
      this.settingsService.getBranches(),
      this.settingsService.getOrganizations(),
      this.storeService.getCurrentAccountRoles()
    ])
      .pipe(take(1))
      .subscribe(([isDealer, account, branch, branches, orgs, roles]: [boolean, Account, Branch, Branch[], Organization[], CurrentAccountRoles]) => {

        this.accountManufacturer = account.manufacturer;
        this.isSystemAdmin = account.accountType === 'SYSTEM_ADMIN';
        this.isDealer = isDealer;
        this.branchId = branch.id; // only set if invite from branch creation

        this.organizations = orgs;

        if (this.permissions.cannotReadBranches() && this.isDealer) {
          this.branches = this.settingsService.filterBranchesByRoleNotUser(branches, roles);
        } else {
          // manufacturers and dealer super admins see all org branches
          this.branches = branches;
        }

        this.dropdownData.branches = this.branches.map((b) => ({ title: b.name, value: b.id }));
        this.dropdownData.organizations = this.organizations
          .map((org) => ({ title: `${org.name} (${org.id})`, value: org.id }))
          .sort((a, b) => a.title.localeCompare(b.title));

        this.setupForm();
        this.storeService.setFormReady(true);
      });
  }

  setupForm(): void {
    let role;
    let branches;

    if (this.branchId) {
      branches = [[this.branchId]];
      role = ['USER'];
    } else {
      branches = this.isDealer ? ['', [Validators.required]] : [];
      if (!this.isSystemAdmin) {
        role = ['', [Validators.required]];
      }
    }

    this.form = this.fb.group({
      invitees: this.fb.array(
        !this.branchId ? [this.createInvitee(true)]
          : [this.createInvitee(true), this.createInvitee(), this.createInvitee()]
      ),
      branches,
      role
    });

    if (this.isSystemAdmin) {
      this.form.addControl('accountType', new FormControl('', Validators.required));
      this.form.addControl('organization', new FormControl('', Validators.required));
    }
  }

  onAccountTypeChange(event: any) {
    const organizationControl = this.form.get('organization');

    if (event === 'MANUFACTURER') {
      organizationControl?.setValue(this.accountManufacturer);
      organizationControl?.disable();
    } else {
      organizationControl?.setValue('');
      organizationControl?.enable();
    }
  }

  createInvitee(emailMandatory = false): UntypedFormGroup {
    const emailValidators = [Validators.pattern(VALIDATORS.PATTERN.MAIL)].concat(
      emailMandatory ? [Validators.required] : []
    );

    return this.fb.group({
      email: ['', emailValidators]
    });
  }

  addInvitee(): void {
    this.invitees.push(this.createInvitee());
  }

  removeInvitee(index: number): void {
    if (this.invitees.controls.length < 2 || index < 0) return;

    this.invitees.removeAt(index);

    if (index === 0) {
      const firstEmailInput = this.invitees.controls[index].get('email') as UntypedFormControl;

      firstEmailInput.setValidators([Validators.pattern(VALIDATORS.PATTERN.MAIL), Validators.required]);
      firstEmailInput.updateValueAndValidity();
    }
  }

  setPending(_val: boolean): void {
    this.form.disable();
    (this.form.statusChanges as any).emit('INVALID');
  }
}
